import React from 'react';
import clsx from 'clsx';

import OverlayTooltip from '../OverlayTooltip';

const iconsMap: { [x in IconType]: string } = {
  calendar: 'fa-regular fa-calendar',
  horn: 'fas fa-bullhorn',
  list: 'fas fa-tasks',
  rocket: 'fas fa-rocket',
  eye: 'fas fa-eye',
  people: 'fas fa-users',
  messages: 'fas fa-comments',
  hook: 'fas fa-bolt',
  document: 'fas fa-file-alt',
  chart: 'fas fa-chart-bar',
  refresh: 'fas fa-sync',
  edit: 'fas fa-edit',
  delete: 'fas fa-trash',
  plus: 'fas fa-plus',
  question: 'fas fa-question',
  play: 'fas fa-play',
  broom: 'fas fa-broom',
  loading: 'fas fa-spinner animate-spin',
  link: 'fas fa-arrow-up-right-from-square',
  send: 'fas fa-paper-plane',
  required: 'fas fa-asterisk',
  copy: 'fas fa-copy',
  eraser: 'fas fa-eraser',
  up: 'fas fa-arrow-up',
  down: 'fas fa-arrow-down',
  left: 'fas fa-arrow-left',
  right: 'fas fa-arrow-right',
  clear: 'fa-solid fa-xmark',
  bars: 'fa-solid fa-bars',
  columns: 'fa-solid fa-table-columns',
  prompt: 'fa-solid fa-wand-magic-sparkles',
  yes: 'fa-solid fa-check',
  no: 'fa-solid fa-xmark',
  dash: 'fa-solid fa-minus',
  share: 'fa-solid fa-share',
  upload: 'fa-solid fa-upload',
  download: 'fa-solid fa-download',
  more: 'fa-solid fa-ellipsis-vertical',
  caretup: 'fa-solid fa-caret-up',
  caretdown: 'fa-solid fa-caret-down',
  angleup: 'fa-solid fa-angle-up',
  angledown: 'fa-solid fa-angle-down',
  bell: 'fa-regular fa-bell',
  speaker: 'fa-solid fa-volume-high',
  voicemail: 'fa-solid fa-voicemail',
};

const iconsStylesMap: { [x in IconStyle]: string } = {
  default: '',
  danger: 'text-[#ef4444]',
};

type IconStyle = 'default' | 'danger';

type IconProps = {
  label?: string;
  className?: string;
  iconClassName?: string;
  icon?: IconType;

  width?: boolean;
  inline?: boolean;

  tooltip?: string;

  iconStyle?: IconStyle;
  onClick?: () => void;
};

const Icon: React.FC<IconProps> = ({
  className,
  iconClassName,
  icon,
  label,
  tooltip,
  iconStyle = 'default',
  width = false,
  inline = false,
  onClick,
}) => {
  if (!icon) return null;

  const element = (
    <span
      className={clsx(
        className,
        width && 'w-[2rem]',
        'leading-none',
        width ? (inline ? 'inline' : 'block') : ''
      )}
    >
      <i
        onClick={onClick}
        className={clsx(
          iconsMap[icon],
          iconClassName,
          'align-middle',
          iconsStylesMap[iconStyle] || iconStyle
        )}
      />
      {!!label && <span className="text-tiny ml-sm">{label}</span>}
    </span>
  );

  return tooltip ? (
    <OverlayTooltip tooltip={tooltip}>{element}</OverlayTooltip>
  ) : (
    element
  );
};

type RoundIconContainerProps = {
  children: React.ReactNode;
  className?: string;
};

export const RoundIconContainer: React.FC<RoundIconContainerProps> = ({
  children,
  className,
}) => {
  return (
    <span
      className={clsx(
        className,
        'bg-text text-tiny text-bg size-7 rounded-full',
        'inline-flex items-center justify-center ml-sm cursor-pointer'
      )}
    >
      {children}
    </span>
  );
};

export default React.memo(Icon) as typeof Icon;
